import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    BookingRemark,
    BookingStatusChangeError,
    IBookingExpressCheckOutResult,
    IBookingSalesAndPaymentsResult,
} from "@/logic/api";

export type ExpressCheckoutMode =
    | "SCANNING"
    | "VALIDATING"
    | "SHOWING_BOOKING_INFO"
    | "CHECKING_OUT"
    | "ERROR";

export interface ExpressCheckoutState {
    mode: ExpressCheckoutMode;
    barcode: string;
    error: string | null;
    scanned: IBookingExpressCheckOutResult[];
    currentBooking: IBookingExpressCheckOutResult | null;
    customerRemarks: BookingRemark[];
    salesAndPayments: IBookingSalesAndPaymentsResult | null;
    preValidationErrors: BookingStatusChangeError[];
    dialogErrorMessage: string | null;
    earlyReturnRequestedBookingIds: number[];
}

const initialState: ExpressCheckoutState = {
    mode: "SCANNING",
    barcode: "",
    error: null,
    scanned: [],
    currentBooking: null,
    customerRemarks: [],
    salesAndPayments: null,
    preValidationErrors: [],
    dialogErrorMessage: null,
    earlyReturnRequestedBookingIds: [],
};

const expressCheckoutSlice = createSlice({
    name: "expressCheckout",
    initialState,
    reducers: {
        reset: () => initialState,
        // Reset UI back to scanning but keep the scanned history
        resetButKeepHistory: (state) => {
            state.mode = 'SCANNING';
            state.barcode = '';
            state.error = null;
            state.currentBooking = null;
            state.customerRemarks = [];
            state.salesAndPayments = null;
            state.preValidationErrors = [];
            state.dialogErrorMessage = null;
            // intentionally keep earlyReturnRequestedBookingIds across scans
        },
        setMode: (state, action: PayloadAction<ExpressCheckoutMode>) => {
            state.mode = action.payload;
            if (action.payload !== "ERROR") {
                state.error = null;
            }
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setBarcode: (state, action: PayloadAction<string>) => {
            state.barcode = action.payload;
        },
        setCurrentBooking: (state, action: PayloadAction<IBookingExpressCheckOutResult | null>) => {
            state.currentBooking = action.payload;
        },
        setSalesAndPayments: (state, action: PayloadAction<IBookingSalesAndPaymentsResult | null>) => {
            state.salesAndPayments = action.payload;
        },
        setCustomerRemarks: (state, action: PayloadAction<BookingRemark[]>) => {
            state.customerRemarks = action.payload;
        },
        setPreValidationErrors: (state, action: PayloadAction<BookingStatusChangeError[]>) => {
            state.preValidationErrors = action.payload;
        },
        addScanned: (state, action: PayloadAction<IBookingExpressCheckOutResult>) => {
            state.scanned = [action.payload, ...state.scanned];
        },
        setDialogErrorMessage: (state, action: PayloadAction<string | null>) => {
            state.dialogErrorMessage = action.payload;
        },
        markEarlyReturnRequested: (state, action: PayloadAction<number>) => {
            const bookingId = action.payload;
            if (!state.earlyReturnRequestedBookingIds.includes(bookingId)) {
                state.earlyReturnRequestedBookingIds = [bookingId, ...state.earlyReturnRequestedBookingIds];
            }
        },
    },
});

export const {
    reset,
    resetButKeepHistory,
    setMode,
    setError,
    setBarcode,
    setCurrentBooking,
    setSalesAndPayments,
    setCustomerRemarks,
    setPreValidationErrors,
    addScanned,
    setDialogErrorMessage,
    markEarlyReturnRequested,
} = expressCheckoutSlice.actions;

export default expressCheckoutSlice.reducer;

